import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { useContext } from "react";
import { FlatList } from "react-native-gesture-handler";
import { Image, Pressable, View } from "react-native";
import { PlaceColors } from "../helpers/PlaceColors";
import { PlaceText } from "./PlaceText";
import { TextShadow, DefaultShadow } from "../helpers/DefaultShadows";
import { makeIconFilterButton } from "../helpers/Components";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { BottomNavigationPadding } from "./FindifyNavigation";


const textDateStyle = {
    marginTop: 5,
    fontSize: 12,
};

export type FindifyListItem = {
    id: string,
    image?: string,
    title: string,
    resume?: string,
    date?: string,
    onPress: () => void,
}

type BaseFindifyListType = {
    items: FindifyListItem[],
    header?: any,
    keyExtractor?: (item: any, index: any) => any,
    style?: any,
    selectLabel?: string,
    onEndReached?: () => void,
    onEndReachedThreshold?: number,
    actions?: JSX.Element,
    enableGrid?: boolean,
    numberOfColumns?: number,
    renderLine?: (item: any, index: any) => JSX.Element
    renderBox?: (item: any, index: any) => JSX.Element
}

export const BaseFindifyList = ({
    items,
    header,
    selectLabel,
    keyExtractor,
    style,
    onEndReached,
    onEndReachedThreshold,
    actions,
    enableGrid,
    renderLine,
    renderBox,
    numberOfColumns
}: BaseFindifyListType) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const insets = useSafeAreaInsets();
    const gridCols = numberOfColumns ?? 2;
    const showGrid = enableGrid && findifyDataContext.showListsGrid;
    return <>
        <FlatList
            key={showGrid ? 'ListGrid' : 'ListLine'}
            data={items}
            numColumns={showGrid ? gridCols : 1}
            ListHeaderComponent={() => header}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            onEndReached={onEndReached}
            onEndReachedThreshold={onEndReachedThreshold ?? 0.7}
            renderItem={({ item, index }) => {
                if (showGrid) {
                    if (renderBox) {
                        return renderBox(item, index)
                    } else {
                        return makeItemBox(item, index, gridCols, findifyDataContext)
                    }
                } else {
                    if (renderLine) {
                        return renderLine(item, index)
                    } else {
                        return makeItemLine(item, index, selectLabel, findifyDataContext)
                    }
                }
            }}
            bounces={false}
            keyExtractor={keyExtractor != null ? keyExtractor : (item, index) => index.toString()}
            contentContainerStyle={[{
                paddingBottom: insets.bottom + BottomNavigationPadding,
            }, style]}
        />
        <View style={{ position: 'absolute', bottom: insets.bottom + 62, right: 10, display: 'flex', gap: 4 }}>
            {actions}
            {enableGrid && makeIconFilterButton(() => findifyDataContext.setShowListsGrid(!findifyDataContext.showListsGrid), `${findifyDataContext.showListsGrid ? 'grid' : 'list'}-outline`, false, findifyDataContext)}
        </View>
    </>
}

const makeItemBox = (item: FindifyListItem, index: number, numberOfColumns: number, findifyDataContext: any) => {
    const imageRadius = 16;
    return <Pressable
        key={'news_index_' + index}
        style={[{
            flex: 1 / numberOfColumns,
            aspectRatio: 1,
            backgroundColor: PlaceColors(findifyDataContext).background.get(),
            margin: 6,
            borderRadius: imageRadius,
        }, DefaultShadow]}
        onPress={item.onPress} >
        <View style={{
            flex: 1,
            aspectRatio: 1,
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            borderRadius: imageRadius,
        }}>
            {!!item.image && <Image style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
            }} source={{
                uri: item.image,
            }} />}
            {/* Dark overlay for text contrast */}
            <View style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '50%', backgroundColor: 'rgba(0,0,0,0.5)' }} />
            <PlaceText
                style={[
                    {
                        fontWeight: 'bold',
                        fontSize: 18,
                        padding: 8,
                        color: 'white',
                        position: 'absolute',
                        bottom: 0,
                        width: '100%',
                        textAlign: 'center',
                    },
                    TextShadow
                ]}>
                {item.title}
            </PlaceText>
        </View>
    </Pressable>;
}

export const makeItemLine = (item: FindifyListItem, index: number, selectLabel: string, findifyDataContext: any) => {
    const backgroundColor = PlaceColors(findifyDataContext).background.get();
    return <Pressable key={'news_index_' + index} onPress={item.onPress} >
        <View style={[{ 
                backgroundColor: backgroundColor, 
                width: 'auto', 
                marginHorizontal: 15, 
                marginVertical: 8,
                padding: 15, 
                borderRadius: 16,
            }, DefaultShadow]}>
            <View style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
                {!!item.image && <Image style={{
                    width: 90,
                    height: 90,
                    objectFit: 'cover',
                    borderRadius: 12,
                }} source={{
                    uri: item.image,
                }} />}
                <View
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}>
                    {!!item.title && <PlaceText style={{ fontWeight: 'bold', fontSize: 16, color: PlaceColors(findifyDataContext).text.get() }}>{item.title}</PlaceText>}
                    {!!item.date && <PlaceText style={[textDateStyle, { color: PlaceColors(findifyDataContext).text.withAlpha(200), marginTop: 2 }]}>{item.date}</PlaceText>}
                    {!!item.resume && <PlaceText
                        numberOfLines={2}
                        style={{
                            marginTop: 6,
                            fontSize: 13,
                            color: PlaceColors(findifyDataContext).text.withAlpha(160),
                        }} >{item.resume}</PlaceText>}
                </View>
            </View>
            {!!selectLabel && <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 10 }}>
                <View style={{ backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(15), paddingHorizontal: 12, paddingVertical: 6, borderRadius: 12 }}>
                    <PlaceText style={{ fontSize: 12, color: PlaceColors(findifyDataContext).primary.get(), fontWeight: 'bold' }}>{selectLabel}</PlaceText>
                </View>
            </View>}
        </View>
    </Pressable>;
}